import React, { useEffect, useState, useMemo, useContext } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import TableCell from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import CloseIcon from '@mui/icons-material/Close';

import { AdminAxios as Axios } from '../../Assets/Axios/Axios';

import { COLORS } from '../../Constant/COLORS';
import { ORDER_STATUS } from '../../Constant/Enums';

import ToastContext from '../../Context/ToastContext';

import { toCapitalize } from '../../Helpers';

import { BookingOrderHistory } from '../../Server/Query';

import SearchField from './Components/SearchField';
import CustomTable from './Components/CustomTable';

const BookingRequests = () => {
  const { showToastMessage } = useContext(ToastContext);

  const [tableRecords, setTableRecords] = useState([]);
  const [filterBy, setFilterBy] = useState({
    status: '',
    serviceName: '',
    date: '',
  });

  const filteredTableRecords = useMemo(
    () =>
      tableRecords.filter(order => {
        let isMatchSearch, isMatchStatus, isMatchDate;
        isMatchSearch = isMatchStatus = isMatchDate = true;

        if (filterBy.serviceName) {
          const isMatchSearchByServiceName = order['Service Name']
            .toLowerCase()
            .startsWith(filterBy.serviceName.toLowerCase());

          const isMatchSearchByOrderId = order.orderId
            .toLowerCase()
            .startsWith(filterBy.serviceName.toLowerCase());

          isMatchSearch = isMatchSearchByServiceName || isMatchSearchByOrderId;
        }

        if (filterBy.status)
          isMatchStatus = order.status === toCapitalize(filterBy.status);

        if (filterBy.date)
          isMatchDate =
            new Date(order['Date and time']).setHours(0, 0, 0, 0) ===
            new Date(filterBy.date).setHours(0, 0, 0, 0);

        return isMatchSearch && isMatchStatus && isMatchDate;
      }),
    [filterBy.date, filterBy.serviceName, filterBy.status, tableRecords]
  );

  const generateTableRecords = useMemo(
    () =>
      filteredTableRecords.map(row => (
        <TableRow key={row.id} hover>
          <TableCell>{row.no}</TableCell>
          <TableCell>{row.orderId}</TableCell>
          <TableCell>{row['Service Name']}</TableCell>
          <TableCell>{row.name}</TableCell>
          <TableCell>{row.note}</TableCell>
          <TableCell>{row['Date and time']}</TableCell>
          <TableCell>{row.status}</TableCell>
        </TableRow>
      )),
    [filteredTableRecords]
  );

  useEffect(() => {
    Axios(BookingOrderHistory)
      .then(({ data, errors }) => {
        if (errors) {
          showToastMessage('Something went wrong while trying to load.');
          return undefined;
        }

        const orders = (data?.Orders ?? []).map(parseOrder);
        setTableRecords(orders);
      })
      .catch(() => showToastMessage('Something went wrong while trying to load.'));
  }, [showToastMessage]);

  const handleChange = ({ target: { name, value } }) => {
    setFilterBy(currentState => ({ ...currentState, [name]: value }));
  };

  return (
    <Box px={{ xs: 2.5, md: 5 }} my={{ xs: 2.5, md: 5 }}>
      <Box
        display='grid'
        gridTemplateColumns={{ xs: 'auto', sm: 'repeat(3,1fr)' }}
        gap={{ xs: 1, sm: 3 }}
        mb={3}
      >
        <SearchField
          name='serviceName'
          value={filterBy.serviceName}
          onChange={handleChange}
        />

        <Stack alignItems='center' direction='row' gap={1}>
          <Typography
            component='label'
            id='filterBy'
            color='rgba(0 0 0/.3)'
            fontWeight={500}
          >
            Status
          </Typography>
          <Select
            sx={{ flexGrow: 1 }}
            name='status'
            value={filterBy.status}
            onChange={handleChange}
            size='small'
            labelId='filterBy'
            IconComponent={null}
            endAdornment={
              <InputAdornment position='end' sx={{ marginInlineEnd: -1 }}>
                <IconButton
                  onClick={() =>
                    setFilterBy(currentState => ({
                      ...currentState,
                      status: '',
                    }))
                  }
                >
                  <CloseIcon fontSize='small' />
                </IconButton>
              </InputAdornment>
            }
          >
            {Object.values(ORDER_STATUS).map(status => (
              <MenuItem value={status} key={status}>
                {toCapitalize(status)}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <Stack alignItems='center' direction='row' gap={1}>
          <Typography
            color='rgba(0 0 0/.3)'
            fontWeight={500}
            component='label'
            id='filterBy'
          >
            Order Date
          </Typography>
          <TextField
            sx={{ flexGrow: 1 }}
            type='date'
            name='date'
            value={filterBy.date}
            onChange={handleChange}
            InputProps={{ sx: { bgcolor: COLORS.white3 } }}
          />
        </Stack>
      </Box>

      <CustomTable
        px={4}
        mb={3}
        height='400px'
        tableHeadings={[
          'No.',
          'Order ID',
          'Service Name',
          'Prof. Name',
          'Service Note',
          'Order Date ',
          'Order Status',
        ]}
      >
        {generateTableRecords}
      </CustomTable>
    </Box>
  );
};

function parseOrder(order, index) {
  const dateFormatter = Intl.DateTimeFormat('default', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });

  const no = (index + 1).toString().padStart(2, '0');

  return {
    no,
    id: order.id,
    orderId: order.orderRef ?? order.order_id ?? order.id,
    name: order.Service?.Professional?.first_name ?? 'Not available',
    note: order.note ?? '',
    status: toCapitalize(order.status ?? ''),
    'Service Name': order.Service?.name
      ? toCapitalize(order.Service.name)
      : 'Not available',
    'Date and time': order.date ? dateFormatter.format(new Date(order.date)) : '',
  };
}

export default BookingRequests;
